import { useState } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../components/Footer";
import Header from "../components/Header";
import NewsletterSignup from "../components/NewsletterSignup";
import "../Style.css";
import "./CommunityResources.css";

export default function CommunityResourcesPage() {
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);

  const resourceCategories = [
    {
      category: "Learn More About What a Financial Coach Can Do for You",
      links: [
        {
          title: "What is a Financial Coach?",
          url: "https://www.bankrate.com/investing/financial-advisors/what-is-a-financial-coach/",
          description: "An overview from Bankrate.",
        },
        {
          title: "The Role of a Financial Coach",
          url: "https://www.financialeducatorscouncil.org/what-is-a-financial-coach/",
          description: "Insights from the Financial Educators Council.",
        },
        {
          title: "Coach vs. Advisor vs. Planner",
          url: "https://www.thrivent.com/insights/financial-planning/financial-advisor-vs-financial-planner-vs-financial-coach-key-differences-explained",
          description: "Key differences explained by Thrivent.",
        },
      ],
    },
    {
      category: "What are Money Scripts?",
      links: [
        {
          title: "What are money scripts?",
          url: "https://www.truist.com/money-mindset/principles/mind-money-connection/how-money-scripts-shape-your-financial-habits",
          description: "Learn how money scripts shape your financial habits.",
        },
        {
          title:
            "Top 10 Money Scripts that could derail your financial wellness",
          url: "https://www.bradklontz.com/moneyscripts",
          description:
            "Identify common money scripts that may impact your financial health.",
        },
        {
          title: "What is YOUR money script?",
          url: "https://www.bradklontz.com/moneyscriptstest",
          description:
            "Take the assessment to discover your personal money scripts.",
        },
      ],
    },
    {
      category: "Free Spreadsheet Budget Templates",
      links: [
        {
          title: "Microsoft Office Templates",
          url: "https://create.microsoft.com/en-us/templates/budgets",
          description: "A variety of budget templates for Excel.",
        },
        {
          title: "Google Sheets Template",
          url: "https://docs.google.com/spreadsheets/d/1YlGTsomx1Y47jdc9HMdV4iVLP9vtZEcNJZQWHLTc1jk/edit?gid=0#gid=0",
          description: "A comprehensive budget planner for Google Sheets.",
        },
        {
          title: "GooDocs Budget Templates",
          url: "https://thegoodocs.com/freebies/budget-templates/",
          description:
            "Free budget templates for Google Docs and other formats.",
        },
      ],
    },
    {
      category: "Free Access to Credit Reports & Scores",
      links: [
        {
          title: "Annual Credit Report",
          url: "https://www.annualcreditreport.com/index.action",
          description: "Official site for free annual credit reports.",
        },
        {
          title: "Understanding FICO Scores",
          url: "https://www.myfico.com/credit-education/what-is-a-fico-score",
          description: "Learn what a FICO score is and why it matters.",
        },
      ],
    },
    {
      category: "Free Financial Literacy Resources",
      links: [
        {
          title: "Resources for Youth",
          url: "https://www.financialeducatorscouncil.org/complimentary-financial-literacy-resources-for-youth/",
          description:
            "Complimentary resources from the Financial Educators Council.",
        },
        {
          title: "Khan Academy",
          url: "https://www.khanacademy.org/college-careers-more/financial-literacy",
          description: "Free courses on financial literacy and more.",
        },
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Free Financial Resources & Tools | Community Resources | Own Your
          Money
        </title>
        <meta
          name="description"
          content="Explore free community resources, budgeting templates, and financial literacy tools curated by Own Your Money. Access credit report links, financial education sites, and money management guides."
        />
        <meta
          name="keywords"
          content="free financial resources, budgeting templates, money scripts, credit report access, financial literacy tools, financial coach resources, community finance help, financial education, North Carolina"
        />
        <link
          rel="canonical"
          href="https://www.ownyourmoney.biz/communityresources"
        />

        <meta
          property="og:site_name"
          content="Own Your Money Financial Coaching"
        />
        <meta
          property="og:title"
          content="Community Resources | Free Financial Tools & Guides"
        />
        <meta
          property="og:description"
          content="Free financial literacy resources, credit report links, and budgeting templates curated by Own Your Money to help individuals and families improve financial confidence."
        />
        <meta
          property="og:url"
          content="https://www.ownyourmoney.biz/communityresources"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://www.ownyourmoney.biz/og-image.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="geo.region" content="US-NC" />
        <meta name="geo.country" content="United States" />
        <meta name="geo.placename" content="Holly Springs, NC" />
        <meta name="geo.position" content="35.65;-78.83" />
        <meta name="ICBM" content="35.65, -78.83" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="en-US" />

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://www.ownyourmoney.biz/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Community Resources",
          "item": "https://www.ownyourmoney.biz/communityresources"
        }
      ]
    }
    `}
        </script>

        {/* Combined FinancialService + Organization + ItemList Schema */}
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "FinancialService",
      "name": "Own Your Money LLC",
      "url": "https://www.ownyourmoney.biz",
      "logo": "https://www.ownyourmoney.biz/static/media/logo.a289caf420b85a4e241ff32552050a01.svg",
      "image": "https://www.ownyourmoney.biz/og-image.png?v=2",
      "description": "Personalized financial coaching and community resources helping individuals and families build financial confidence and literacy.",
      "telephone": "+1-919-285-1689",
      "email": "OwnYourMoneyNC@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Holly Springs",
        "addressRegion": "NC",
        "addressCountry": "US"
      },
      "areaServed": [
        { "@type": "State", "name": "North Carolina" },
        { "@type": "Country", "name": "United States" }
      ],
      "founder": {
        "@type": "Person",
        "name": "Wendy Toh",
        "jobTitle": "Certified Personal Financial Wellness Consultant"
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+1-919-285-1689",
          "contactType": "customer service",
          "areaServed": ["US", "NC"],
          "availableLanguage": ["English"],
          "email": "OwnYourMoneyNC@gmail.com"
        }
      ],
      "sameAs": [
        "https://www.linkedin.com/in/wendytoh",
        "https://www.facebook.com/ownyourmoney"
      ],
      "hasPart": {
        "@type": "ItemList",
        "name": "Community Financial Resources",
        "itemListElement": [
          {
            "@type": "CreativeWork",
            "position": 1,
            "name": "What is a Financial Coach?",
            "url": "https://www.bankrate.com/finance/financial-coach/"
          },
          {
            "@type": "CreativeWork",
            "position": 2,
            "name": "The Role of a Financial Coach",
            "url": "https://www.financialeducatorscouncil.org/what-is-a-financial-coach/"
          },
          {
            "@type": "CreativeWork",
            "position": 3,
            "name": "Coach vs. Advisor vs. Planner",
            "url": "https://www.thrivent.com/insights/money-basics/coach-vs-advisor-vs-planner.html"
          },
          {
            "@type": "CreativeWork",
            "position": 4,
            "name": "What Are Money Scripts?",
            "url": "https://www.klontzconsulting.com/money-scripts/"
          },
          {
            "@type": "CreativeWork",
            "position": 5,
            "name": "Top 10 Money Scripts that Could Derail Your Financial Wellness",
            "url": "https://www.financialeducatorscouncil.org/top-10-money-scripts/"
          },
          {
            "@type": "CreativeWork",
            "position": 6,
            "name": "What is YOUR Money Script? Assessment",
            "url": "https://www.yourmentalwealthadvisors.com/money-script-assessment/"
          },
          {
            "@type": "CreativeWork",
            "position": 7,
            "name": "Microsoft Office Budget Templates",
            "url": "https://templates.office.com/en-us/budgets"
          },
          {
            "@type": "CreativeWork",
            "position": 8,
            "name": "Google Sheets Budget Template",
            "url": "https://docs.google.com/spreadsheets"
          },
          {
            "@type": "CreativeWork",
            "position": 9,
            "name": "GooDocs Budget Templates",
            "url": "https://gootemplates.com/free-budget-templates/"
          },
          {
            "@type": "CreativeWork",
            "position": 10,
            "name": "Annual Credit Report",
            "url": "https://www.annualcreditreport.com/"
          },
          {
            "@type": "CreativeWork",
            "position": 11,
            "name": "Understanding FICO Scores",
            "url": "https://www.myfico.com/credit-education/credit-scores"
          },
          {
            "@type": "CreativeWork",
            "position": 12,
            "name": "Resources for Youth - Financial Educators Council",
            "url": "https://www.financialeducatorscouncil.org/financial-literacy-for-youth/"
          },
          {
            "@type": "CreativeWork",
            "position": 13,
            "name": "Khan Academy Financial Literacy Courses",
            "url": "https://www.khanacademy.org/college-careers-more/personal-finance"
          }
        ]
      },
      "serviceType": [
        "Financial Coaching",
        "Financial Literacy Education",
        "Budget Planning Support"
      ],
      "priceRange": "$$"
    }
    `}
        </script>
      </Helmet>

      <Header />
      <main className="resources-main">
        <div className="resources-container">
          <section className="resources-header">
            <h1>Community Resources</h1>
            <p>
              Access complimentary financial resources and community materials.
            </p>
          </section>

          {/* Newsletter Banner */}
          <section className="newsletter-banner">
            <div className="newsletter-content">
              <h2>Register for a Quarterly Hints and Tips Newsletter</h2>
              <button
                className="primary-btn newsletter-btn"
                onClick={() => setShowNewsletterModal(true)}
              >
                Sign Up Now
              </button>
              <p className="newsletter-subtitle">No spam, ever. We promise.</p>
            </div>
          </section>

          {/* Useful Links Section */}
          {resourceCategories.map((category, index) => (
            <section key={index} className="resources-section">
              <h2>{category.category}</h2>
              <div className="links-grid">
                {category.links.map((link, linkIndex) => (
                  <div key={linkIndex} className="resource-card">
                    <h3>{link.title}</h3>
                    <p>{link.description}</p>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="resource-link"
                    >
                      Access Resource →
                    </a>
                  </div>
                ))}
              </div>
            </section>
          ))}

          <section className="resources-section">
            <h2>Client Agreements</h2>
            <ul className="resource-list">
              <li className="resource-item">
                <a
                  href="https://docs.google.com/document/d/1om2y-nTlyJTtLWEn8YrlyfciBeSJCI_E/edit?usp=sharing&ouid=113791286214041964028&rtpof=true&sd=true"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Sample Client Contract →
                </a>
                <p>Review a sample of our client coaching agreement.</p>
              </li>
            </ul>
          </section>
        </div>
      </main>
      <Footer />
      {showNewsletterModal && (
        <NewsletterSignup onClose={() => setShowNewsletterModal(false)} />
      )}
    </>
  );
}
